import { useEffect } from 'react';
import { type ecommerceEvents } from '@bedrock/analytics/events';
import { type AnalyticsObject, type EcommerceItem } from '@bedrock/analytics/types';

type AnyRoadProduct = {
  id: number;
  name: string;
  item_name?: string;
  affiliation?: string; // AnyRoad
  price?: string;
  quantity: number;
  currency: string;
  discount: number;
  item_category: string;
  item_variant: string;
  coupon?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_id?: string;
  item_list_name?: string;
};

type AnyRoadBaseEvent<
  E extends string,
  T extends object,
  EventName extends string | undefined = undefined,
> = {
  event: E;
  event_name?: EventName;
  ecommerce: {
    currency: string;
    currencyCode: string;
    value: number;
  } & T;
};

type AnyRoadDetailsEventData = AnyRoadBaseEvent<
  'detail',
  {
    detail: {
      actionField: {
        affiliation: string;
      };
      products: AnyRoadProduct[];
    };
  }
>;

type AnyRoadCheckoutEventData = AnyRoadBaseEvent<
  'checkout' | 'begin_checkout',
  {
    checkout: {
      actionField: {
        affiliation: string;
        value: number;
        step: number;
      };
      products: AnyRoadProduct[];
    };
  },
  'begin_checkout' | 'add_shipping_info' | 'additional_questions' | 'add_payment_info'
>;

type AnyRoadAddToCartEventData = AnyRoadBaseEvent<
  'add_to_cart',
  { add: { products: AnyRoadProduct[] } }
>;

type AnyRoadPurchaseEventData = AnyRoadBaseEvent<
  'purchase',
  {
    purchase: {
      products: AnyRoadProduct[];
      transactionId?: string | number;
      tax?: string;
      coupon?: string;
    };
  }
>;

type AnyRoadEventData =
  | AnyRoadDetailsEventData
  | AnyRoadCheckoutEventData
  | AnyRoadAddToCartEventData
  | AnyRoadPurchaseEventData;

export type EcommerceBaseInfo = {
  brand: string;
};

const removeUndefined = <T extends object>(obj: T): T => {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
};

const mapEcommerceItems = (
  product: AnyRoadProduct,
  { brand }: EcommerceBaseInfo,
): EcommerceItem => {
  return removeUndefined({
    itemId: product.id.toString(),
    itemName: product.name || product.item_name || '',
    quantity: product.quantity,
    itemBrand: brand,
    affiliation: product.affiliation || 'AnyRoad',
    coupon: product.coupon,
    discount: product.discount,
    itemCategory: product.item_category,
    itemCategory2: product.item_category2,
    itemCategory3: product.item_category3,
    itemCategory4: product.item_category4,
    itemCategory5: product.item_category5,
    itemListId: product.item_list_id,
    itemListName: product.item_list_name,
    itemVariant: product.item_variant,
    price: product.price ? Number.parseFloat(product.price) : undefined,
  });
};

export const useAnyRoadAnalytics = (
  analytics: AnalyticsObject<typeof ecommerceEvents>,
  baseInfo: EcommerceBaseInfo,
) => {
  useEffect(() => {
    // Intercept dataLayer.push to filter out AnyRoad's raw 'detail' event.
    // AnyRoad's script pushes 'detail' directly into dataLayer, which interrupts
    // GTM trigger conditions for add_to_cart and purchase GA4 events.
    // The mapped 'view_item' event (pushed by our handler below) remains unaffected.
    const dataLayer = (globalThis as typeof globalThis & { dataLayer?: unknown[] }).dataLayer;
    const originalPush = dataLayer?.push.bind(dataLayer);

    if (dataLayer && originalPush) {
      dataLayer.push = function filterDetailEvent(...args: unknown[]) {
        const filtered = args.filter(
          (item) =>
            !(
              item &&
              typeof item === 'object' &&
              'event' in item &&
              (item as Record<string, unknown>).event === 'detail'
            ),
        );
        return filtered.length > 0 ? originalPush(...filtered) : 0;
      };
    }

    const handler = (event: MessageEvent<AnyRoadEventData | string | null>) => {
      if (event.origin !== 'https://app.anyroad.com') return;
      if (!(event.data && typeof event.data === 'object' && 'event' in event.data)) return;
      console.info('AnyRoad event data:', event);

      if (event.data.event === 'detail') {
        const data = event.data;
        analytics.viewItem({
          viewItemType: 'Ecommerce',
          currency: data.ecommerce.currencyCode || data.ecommerce.currency,
          items: data.ecommerce.detail.products.map((product) =>
            mapEcommerceItems(product, baseInfo),
          ),
        });
      }

      if (event.data.event === 'add_to_cart') {
        const data = event.data;
        analytics.addToCart({
          addToCartType: 'PLP',
          value: data.ecommerce.value,
          currency: data.ecommerce.currencyCode || data.ecommerce.currency,
          items: data.ecommerce.add.products.map((product) => mapEcommerceItems(product, baseInfo)),
        });
      }

      if (
        event.data.event === 'begin_checkout' ||
        (event.data.event === 'checkout' && event.data.event_name === 'begin_checkout')
      ) {
        const data = event.data;
        analytics.beginCheckout({
          value: data.ecommerce.value,
          currency: data.ecommerce.currencyCode || data.ecommerce.currency,
          items: data.ecommerce.checkout.products.map((product) =>
            mapEcommerceItems(product, baseInfo),
          ),
        });
      }

      if (event.data.event === 'purchase') {
        const data = event.data;
        analytics.purchase(
          removeUndefined({
            transactionId: data.ecommerce.purchase.transactionId?.toString(),
            tax: data.ecommerce.purchase.tax
              ? Number.parseFloat(data.ecommerce.purchase.tax)
              : undefined,
            coupon: data.ecommerce.purchase.coupon,
            value: data.ecommerce.value,
            currency: data.ecommerce.currencyCode || data.ecommerce.currency,
            items: data.ecommerce.purchase.products.map((product) =>
              mapEcommerceItems(product, baseInfo),
            ),
          }),
        );
      }
    };

    globalThis.addEventListener('message', handler);
    return () => {
      globalThis.removeEventListener('message', handler);
      if (dataLayer && originalPush) {
        dataLayer.push = originalPush;
      }
    };
  }, [baseInfo, analytics]);
};
